import React from 'react';
import { convertToProxyUrl } from '../../utils/photoProxy';
import './PostCard.css';

/**
 * Post shape returned by the admin analytics endpoints. Mirrors the fields the
 * app / website post renderer consumes so the card looks like it does in-app.
 */
export interface AdminPost {
  _id: string;
  text?: string;
  createdAt?: string;
  expiresAt?: string | null;
  originLevel?: string | null;
  currentPostLevel?: string | null;
  user?: {
    _id: string;
    displayName?: string;
    username?: string;
    profilePicture?: string;
    publicPicture?: string;
    facePicture?: string;
  } | null;
  location?: {
    place?: { name?: string; type?: string } | null;
    neighborhood?: { name?: string } | null;
  } | null;
  media?: {
    type?: string;
    photo?: string | null;
    video?: string | null;
    thumbnail?: string | null;
  } | null;
  totalPromotes?: number;
  commentCount?: number;
  shares?: number;
  // Lifetime engagement counters (product-analytics events pipeline).
  impressionCount?: number;
  viewCount?: number;
  detailOpenCount?: number;
  photoOpenCount?: number;
  videoOpenCount?: number;
}

const DEFAULT_AVATAR = '/logo192.png';

function formatTimeAgo(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** Split text into segments so @mentions / #hashtags can be highlighted. */
function renderText(text: string): React.ReactNode {
  if (!text) return null;
  const parts = text.split(/(@\w+|#\w+)/g);
  return parts.map((part, i) => {
    if (/^[@#]\w+$/.test(part)) {
      return (
        <span key={i} className="admin-post-token">
          {part}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function pickMedia(post: AdminPost): { type: 'photo' | 'video'; url: string } | null {
  const media = post.media;
  if (!media) return null;
  if (media.type === 'video' && media.video) {
    return { type: 'video', url: media.video };
  }
  const photo = media.photo || null;
  if (photo) return { type: 'photo', url: convertToProxyUrl(photo) || photo };
  return null;
}

export const PostCard: React.FC<{ post: AdminPost }> = ({ post }) => {
  const user = post.user;
  const displayName = user?.displayName || '';
  const username = user?.username || '';
  const displayLabel = displayName || (username ? `@${username}` : 'Anonymous');
  const avatar =
    convertToProxyUrl(user?.publicPicture || user?.profilePicture || user?.facePicture) ||
    DEFAULT_AVATAR;

  const placeName = post.location?.place?.name || '';
  const placeType = post.location?.place?.type || '';
  const neighborhoodName = post.location?.neighborhood?.name || '';
  const originatedInPlace = post.originLevel === 'PLACE' && !!placeName;

  const media = pickMedia(post);
  const timeAgo = formatTimeAgo(post.createdAt);

  let hoursLeft = 0;
  if (post.expiresAt) {
    const exp = new Date(post.expiresAt).getTime();
    if (!Number.isNaN(exp)) {
      hoursLeft = Math.floor((exp - Date.now()) / (1000 * 60 * 60));
    }
  }

  return (
    <div className="admin-post-container">
      {originatedInPlace ? (
        <div className="admin-post-place-banner">
          <div className="admin-post-place-left">
            <span className="admin-post-place-icon" aria-hidden>
              📍
            </span>
            <span className="admin-post-place-name">{placeName}</span>
          </div>
          {placeType ? <span className="admin-post-place-type">{placeType}</span> : null}
        </div>
      ) : neighborhoodName ? (
        <div className="admin-post-place-banner">
          <div className="admin-post-place-left">
            <span className="admin-post-place-icon" aria-hidden>
              🏙️
            </span>
            <span className="admin-post-place-name">{neighborhoodName}</span>
          </div>
        </div>
      ) : null}

      <div className="admin-post-main">
        <img
          src={avatar}
          alt={displayLabel}
          className="admin-post-avatar"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
          }}
        />
        <div className="admin-post-stack">
          <div className="admin-post-header">
            <span className="admin-post-author">{displayLabel}</span>
            <span className="admin-post-time"> · {timeAgo}</span>
            {hoursLeft > 0 ? <span className="admin-post-expiry">{hoursLeft}h</span> : null}
          </div>
          <div className="admin-post-body">
            {post.text ? <div className="admin-post-text">{renderText(post.text)}</div> : null}
            {media ? (
              <div className="admin-post-media">
                {media.type === 'video' ? (
                  <video src={media.url} controls playsInline />
                ) : (
                  <img
                    src={media.url}
                    alt="Post media"
                    onError={(e) => {
                      const el = e.currentTarget.parentElement as HTMLElement | null;
                      if (el) el.style.display = 'none';
                    }}
                  />
                )}
              </div>
            ) : null}
            {!post.text && !media ? <div className="admin-post-empty">(no text)</div> : null}
          </div>
        </div>
      </div>

      <div className="admin-post-footer">
        <span className="admin-post-footer-item">▲ {post.totalPromotes ?? 0}</span>
        <span className="admin-post-footer-item">💬 {post.commentCount ?? 0}</span>
        {post.shares ? <span className="admin-post-footer-item">↗ {post.shares}</span> : null}
        <span className="admin-post-footer-item" title="Feed impressions (scrolled by)">
          👁 {post.impressionCount ?? 0}
        </span>
        <span className="admin-post-footer-item" title="Detail opens">
          🔎 {post.detailOpenCount ?? 0}
        </span>
        {(post.photoOpenCount ?? 0) + (post.videoOpenCount ?? 0) > 0 ? (
          <span className="admin-post-footer-item" title="Full-screen photo / video opens">
            🖼 {(post.photoOpenCount ?? 0) + (post.videoOpenCount ?? 0)}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default PostCard;
